import { Chip, Tooltip } from '@orbitalwatch/design';
import type { ChipTone } from '@orbitalwatch/design';
import type { ReactNode } from 'react';

export interface ChipCloudItem {
    label: string;
    tone?: ChipTone;
    /** Tooltip-less native title (the full scope path behind a short label). */
    title?: string;
}

export interface ChipCloudProps {
    /** Strings read as neutral chips. */
    items: (string | ChipCloudItem)[];
    /** Chips shown before the "+N" overflow chip (its tooltip lists the rest). Default 4; `Infinity` shows all. */
    max?: number;
    /** Not uppercase by default: permissions, scopes and profiles are identifiers. */
    uppercase?: boolean;
    /** Mono labels (permissions, scopes). Default `true`. */
    mono?: boolean;
    /** Shown when `items` is empty (default a muted dash). */
    empty?: ReactNode;
    /** Names the list ("Permissions"). */
    'aria-label'?: string;
}

const normalise = (item: string | ChipCloudItem): ChipCloudItem => (typeof item === 'string' ? { label: item } : item);

/** A wrapping row of small library `Chip`s with a "+N" overflow chip whose `Tooltip` lists the hidden ones. */
export function ChipCloud(props: ChipCloudProps) {
    const items = props.items.map(normalise);
    if (items.length === 0) return <>{props.empty ?? <span className="ark-muted">-</span>}</>;
    const max = props.max ?? 4;
    const shown = items.length > max ? items.slice(0, max) : items;
    const hidden = items.slice(shown.length);
    const uppercase = props.uppercase ?? false;
    const className = props.mono ?? true ? 'ark-chip-mono' : undefined;

    return (
        <ul className="ark-chip-cloud" aria-label={props['aria-label']}>
            {shown.map(item => (
                <li key={item.label}>
                    <Chip size="sm" tone={item.tone ?? 'neutral'} uppercase={uppercase} title={item.title} className={className}>{item.label}</Chip>
                </li>
            ))}
            {hidden.length > 0 ? (
                <li>
                    <Tooltip content={hidden.map(item => item.label).join(', ')} wrapTrigger>
                        <Chip size="sm" variant="outline" uppercase={false} title={`${hidden.length} more`}>{`+${hidden.length}`}</Chip>
                    </Tooltip>
                </li>
            ) : null}
        </ul>
    );
}
